import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { get, map, omit, pick, property } from 'lodash';
import { formatNumber } from 'libphonenumber-js';
import { Icon } from 'semantic-ui-react';

import { Modal, Button } from '../../../../components';
import { MasterForm } from '../form';
import useUserLookup from '../../../../lib/useUserLookup';
import { GET_MASTERS_QUERY } from '../MastersList';

const UPDATE_MASTER_QUERY = gql`
    mutation updateMaster($input: UpdateMasterInput!, $lang: String!) {
        updateMaster(input: $input) {
            master {
                id
                firstName
                lastName
                phone
                email
                status
                type
                gender
                serviceGroups {
                    id
                    name(lang: $lang)
                    type
                }
            }
        }
    }
`;

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.staff.form.existingsError',
        defaultMessage: 'This master does not exist',
    },
    phoneExistingsError: {
        id: 'pages.staff.form.phoneExistingsError',
        defaultMessage: 'Master with the same phone number already exists',
    },
    emailExistingsError: {
        id: 'pages.staff.form.emailExistingsError',
        defaultMessage: 'Master with the same email already exists',
    },
    emailPhoneError: {
        id: 'pages.clients.form.ClientForm.emailPhoneError',
        defaultMessage: 'There are two different users with the provided phone and email',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const formatPhone = phone => (phone ? formatNumber(phone, 'International') : '+371');

function buildInitialValues(master) {
    return {
        firstName: master.firstName || '',
        lastName: master.lastName || '',
        phone: formatPhone(master.phone),
        email: master.email || '',
        status: master.status || 'ACTIVE',
        serviceGroups: map(master.serviceGroups, property('id')),
        gender: master.gender || '',
    };
}

function PersonalInformation({ master, open, onClose, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const { userLookupStatus, userLookupUser, onUserLookup, resetUserLookup } = useUserLookup();

    const methods = useForm({ defaultValues: buildInitialValues(master) });
    const { handleSubmit, reset, setError, getValues, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        if (open) {
            reset(buildInitialValues(master));
        }
    }, [master.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (open && userLookupUser) {
            const fields = ['email', 'firstName', 'lastName', 'gender'];
            reset({
                ...getValues(),
                ...pick(userLookupUser, fields),
                phone: formatPhone(userLookupUser.phone),
            });
        }
    }, [userLookupUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateMaster] = useMutation(UPDATE_MASTER_QUERY, {
        update: (store, response) => {
            const updatedMaster = get(response, 'data.updateMaster.master');
            if (updatedMaster && updatedMaster.id !== master.id) {
                try {
                    const data = store.readQuery({
                        query: GET_MASTERS_QUERY,
                        variables: {
                            salon,
                            search: searchQuery || undefined,
                            lang: currentLanguage,
                        },
                    });
                    const nodes = data.viewer.salon.masters.nodes || [];
                    const idx = nodes.findIndex(m => m.id === master.id);
                    if (idx !== -1) {
                        const nextNodes = [...nodes];
                        nextNodes.splice(idx, 1, updatedMaster);
                        store.writeQuery({
                            query: GET_MASTERS_QUERY,
                            variables: {
                                salon,
                                search: searchQuery || undefined,
                                lang: currentLanguage,
                            },
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salon: {
                                        ...data.viewer.salon,
                                        masters: {
                                            ...data.viewer.salon.masters,
                                            nodes: nextNodes,
                                        },
                                    },
                                },
                            },
                        });
                    }
                } catch (e) {
                    // cache not populated — safe to ignore
                }
            }
        },
    });

    const readOnly = userLookupStatus === 'EXISTS' || (master.type === 'GLOBAL' && !isDirty);

    const onSubmit = async formData => {
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');
        try {
            await updateMaster({
                variables: {
                    input: { salonId: salon, masterId: master.id, ...formData, phone },
                    lang: currentLanguage,
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateMaster: {
                        __typename: 'UpdateMasterPayload',
                        master: {
                            __typename: 'Master',
                            id: master.id,
                            type: master.type,
                            serviceGroups: map(
                                formData.serviceGroups,
                                serviceGroup => ({
                                    __typename: 'ServiceGroups',
                                    id: serviceGroup,
                                    name: '',
                                    type: '',
                                }),
                            ),
                            ...omit(formData, ['serviceGroups']),
                            phone,
                        },
                    },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (
                    graphQLError.name === 'NotExists' &&
                    graphQLError.data?.error === 'ID_DOES_NOT_EXISTS'
                ) {
                    setError('root', { message: intl.formatMessage(intlMessages.existingsError) });
                } else if (graphQLError.name === 'AlreadyExists') {
                    if (graphQLError.data?.error === 'PHONE_ALREADY_EXISTS') {
                        setError('phone', { message: intl.formatMessage(intlMessages.phoneExistingsError) });
                    } else if (graphQLError.data?.error === 'email') {
                        setError('email', { message: intl.formatMessage(intlMessages.emailExistingsError) });
                    }
                } else if (
                    graphQLError.name === 'Forbidden' &&
                    graphQLError.data?.error === 'THERE_ARE_TWO_DIFFERENT_USER_WITH_PROVIDED_PHONE_AND_EMAIL'
                ) {
                    setError('root', { message: intl.formatMessage(intlMessages.emailPhoneError) });
                } else {
                    setError('root', { message: graphQLError.message });
                }
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    <MasterForm
                        readOnly={readOnly}
                        userLookupStatus={userLookupStatus}
                        userLookupUser={userLookupUser}
                        onUserLookup={onUserLookup}
                        resetUserLookup={resetUserLookup}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.staff.details.editPersonalInformation.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.staff.details.editPersonalInformation.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(PersonalInformation);
